import { makeStyles } from "@fluentui/react-components";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";

const PICKER_WIDTH = "720px";
const PICKER_LIST_MAX_HEIGHT = "320px";
const FILE_ICON_SIZE = "32px";
const RADIO_DOT_SIZE = "16px";
const RADIO_DOT_INNER_SIZE = "8px";
const FILTER_CHIP_MIN_HEIGHT = "28px";
const FILE_NAME_FONT_SIZE = designTokens.fontSize.sm;

export const useSourceDocumentPickerDialogStyles = makeStyles({
  surface: {
    width: PICKER_WIDTH,
    maxWidth: "90vw",
    fontFamily: projectIqTokens.fontFamily,
  },
  title: {
    fontSize: designTokens.fontSize.base,
    fontWeight: designTokens.fontWeight.bold,
    lineHeight: String(designTokens.lineHeight.tight),
    color: projectIqTokens.ink,
  },
  subtitle: {
    margin: 0,
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.ink3,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
  },
  searchBox: {
    width: "100%",
    maxWidth: "none",
  },
  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: designTokens.spacing[2],
  },
  groupHeading: {
    margin: 0,
    fontSize: designTokens.fontSize.xxs,
    fontWeight: designTokens.fontWeight.bold,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: projectIqTokens.ink3,
  },
  groupSection: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
    maxHeight: PICKER_LIST_MAX_HEIGHT,
    overflowY: "auto",
    overflowX: "hidden",
    margin: 0,
    padding: `0 ${designTokens.spacing[1]} 0 0`,
    listStyle: "none",
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
    border: "1px solid transparent",
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: "transparent",
    cursor: "pointer",
    width: "100%",
    boxSizing: "border-box",
    minWidth: 0,
    transition: `background-color ${designTokens.transition.fast}, border ${designTokens.transition.fast}`,
    ":hover": {
      backgroundColor: projectIqTokens.tealSoft,
      border: `1px solid ${projectIqTokens.tealLine}`,
    },
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.teal}`,
      outlineOffset: "2px",
    },
  },
  optionSelected: {
    backgroundColor: projectIqTokens.tealSoft,
    border: `1px solid ${projectIqTokens.teal}`,
    ":hover": {
      backgroundColor: projectIqTokens.tealSoft,
    },
  },
  radioDot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: RADIO_DOT_SIZE,
    height: RADIO_DOT_SIZE,
    minWidth: RADIO_DOT_SIZE,
    boxSizing: "border-box",
    borderRadius: designTokens.borderRadius.full,
    border: `1px solid ${projectIqTokens.ink3}`,
    backgroundColor: projectIqTokens.card,
    flexShrink: 0,
  },
  radioDotSelected: {
    border: `2px solid ${projectIqTokens.teal}`,
  },
  radioDotInner: {
    width: RADIO_DOT_INNER_SIZE,
    height: RADIO_DOT_INNER_SIZE,
    borderRadius: designTokens.borderRadius.full,
    backgroundColor: projectIqTokens.teal,
  },
  fileIconWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: FILE_ICON_SIZE,
    height: FILE_ICON_SIZE,
    borderRadius: designTokens.borderRadius.md,
    flexShrink: 0,
  },
  fileIconPdf: {
    backgroundColor: projectIqTokens.rustBg,
    color: projectIqTokens.rust,
  },
  fileIconXls: {
    backgroundColor: projectIqTokens.greenBg,
    color: projectIqTokens.green,
  },
  fileIconPpt: {
    backgroundColor: designTokens.semantic.warningLight,
    color: projectIqTokens.amber,
  },
  fileIconDefault: {
    backgroundColor: projectIqTokens.bg2,
    color: projectIqTokens.ink3,
  },
  optionInfo: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
  },
  optionName: {
    margin: 0,
    fontSize: FILE_NAME_FONT_SIZE,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  optionMeta: {
    margin: 0,
    fontSize: designTokens.fontSize.xxs,
    color: projectIqTokens.ink3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  emptyState: {
    padding: `${designTokens.spacing[6]} ${designTokens.spacing[4]}`,
    textAlign: "center",
    color: projectIqTokens.ink3,
    fontSize: designTokens.fontSize.sm,
    border: `1px dashed ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.md,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: designTokens.spacing[6],
  },
  footer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "nowrap",
    gap: designTokens.spacing[2],
    gridColumn: "1 / -1",
    justifySelf: "stretch",
    width: "100%",
  },
  footerHint: {
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    flexShrink: 0,
    whiteSpace: "nowrap",
    marginRight: "auto",
    gap: designTokens.spacing[1],
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.ink3,
  },
  footerLink: {
    fontSize: "inherit",
    fontWeight: designTokens.fontWeight.semibold,
  },
  footerActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    marginLeft: "auto",
    gap: designTokens.spacing[2],
  },
  countText: {
    color: projectIqTokens.ink3,
    marginLeft: designTokens.spacing[1],
  },
  filterChipCompact: {
    minHeight: FILTER_CHIP_MIN_HEIGHT,
    paddingTop: designTokens.spacing[1],
    paddingBottom: designTokens.spacing[1],
    paddingLeft: designTokens.spacing[2],
    paddingRight: designTokens.spacing[2],
    fontSize: designTokens.fontSize.xs,
  },
  filterChipLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
  },
});
